import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Search, FileText, Clock, CheckCircle, AlertCircle, Eye, Edit, Download, Loader2 } from "lucide-react";
import { formatCurrency } from "@/utils/financial-analysis";

interface CIMEntry {
  id: string;
  dealId: string;
  dealName: string;
  status: "draft_internal" | "pending_review" | "approved" | "sent_to_client";
  createdAt: Date;
  updatedAt: Date;
  financialSummary?: {
    adjustedEbitda: number;
    valuationMid: number;
  };
  content?: {
    executiveSummary: string;
    businessOverview: string;
    financialHighlights: string;
    growthOpportunities: string;
    investmentConsiderations: string;
    transitionPlan: string;
  };
}

export default function CIMEvaluation() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [cims, setCims] = useState<CIMEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCIMs();
  }, []);

  const loadCIMs = async () => {
    try {
      setLoading(true);
      const cimQuery = query(collection(db, "cimData"), orderBy("createdAt", "desc"));
      const snapshot = await getDocs(cimQuery);
      
      const cimList: CIMEntry[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        cimList.push({
          id: doc.id,
          dealId: data.dealId || doc.id,
          dealName: data.dealName || "Unknown Deal",
          status: data.status || "pending_review",
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          financialSummary: data.financialSummary,
          content: data.content,
        });
      });
      
      setCims(cimList);
    } catch (error) {
      console.error("Error loading CIMs:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCIMs = cims.filter((cim) => {
    const matchesSearch = cim.dealName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || cim.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; className: string }> = {
      draft_internal: { label: "Draft", className: "bg-gray-500/20 text-gray-400 border-gray-500/50" },
      pending_review: { label: "Pending Review", className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/50" },
      approved: { label: "Approved", className: "bg-green-500/20 text-green-400 border-green-500/50" },
      sent_to_client: { label: "Sent to Client", className: "bg-blue-500/20 text-blue-400 border-blue-500/50" },
    };
    return config[status] || { label: status, className: "" };
  };

  const getCompleteness = (cim: CIMEntry) => {
    if (!cim.content) return 0;
    const sections = Object.values(cim.content);
    const filled = sections.filter((s) => s && s.length > 50).length;
    return Math.round((filled / sections.length) * 100);
  };

  const getSectionStatus = (cim: CIMEntry) => {
    if (!cim.content) return {};
    return {
      summary: (cim.content.executiveSummary?.length || 0) > 50,
      business: (cim.content.businessOverview?.length || 0) > 50,
      financials: (cim.content.financialHighlights?.length || 0) > 50,
      growth: (cim.content.growthOpportunities?.length || 0) > 50,
      investment: (cim.content.investmentConsiderations?.length || 0) > 50,
      transition: (cim.content.transitionPlan?.length || 0) > 50,
    };
  };

  const countByStatus = (status: string) => cims.filter((c) => c.status === status).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">CIM & Evaluation</h1>
          <p className="text-white/60 mt-1">
            Review and manage Confidential Information Memorandums
          </p>
        </div>
        <Button onClick={loadCIMs} variant="outline" className="border-white/20 text-white hover:bg-white/10">
          <Loader2 className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-yellow-500/20">
                <Clock className="h-6 w-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Pending Review</p>
                <p className="text-2xl font-bold text-white">{countByStatus("pending_review")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-gray-500/20">
                <FileText className="h-6 w-6 text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Draft</p>
                <p className="text-2xl font-bold text-white">{countByStatus("draft_internal")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/20">
                <CheckCircle className="h-6 w-6 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Approved</p>
                <p className="text-2xl font-bold text-white">{countByStatus("approved")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-500/20">
                <AlertCircle className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Sent to Client</p>
                <p className="text-2xl font-bold text-white">{countByStatus("sent_to_client")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            placeholder="Search CIMs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
          />
        </div>
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList className="bg-white/10">
            <TabsTrigger value="all" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">
              All ({cims.length})
            </TabsTrigger>
            <TabsTrigger value="pending_review" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">
              Pending ({countByStatus("pending_review")})
            </TabsTrigger>
            <TabsTrigger value="approved" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">
              Approved ({countByStatus("approved")})
            </TabsTrigger>
            <TabsTrigger value="sent_to_client" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">
              Sent ({countByStatus("sent_to_client")})
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-brand-gold" />
          <span className="ml-3 text-white">Loading CIMs...</span>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredCIMs.length === 0 && (
        <Card className="bg-white/10 border-white/20">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-white/40 mb-4" />
            <p className="text-white/60">No CIMs found matching your criteria</p>
          </CardContent>
        </Card>
      )}

      {/* CIMs List */}
      {!loading && (
        <div className="space-y-4">
          {filteredCIMs.map((cim) => {
            const statusConfig = getStatusBadge(cim.status);
            const completeness = getCompleteness(cim);
            const sections = getSectionStatus(cim);

            return (
              <Card key={cim.id} className="bg-white/10 border-white/20 hover:bg-white/15 transition-colors">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-white">{cim.dealName}</h3>
                        <Badge variant="outline" className={statusConfig.className}>
                          {statusConfig.label}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-4 text-white/60 text-sm mb-4">
                        <span>Created: {cim.createdAt.toLocaleDateString()}</span>
                        {cim.financialSummary && (
                          <>
                            <span>•</span>
                            <span>EBITDA: {formatCurrency(cim.financialSummary.adjustedEbitda)}</span>
                            <span>•</span>
                            <span>Valuation: {formatCurrency(cim.financialSummary.valuationMid)}</span>
                          </>
                        )}
                      </div>
                      
                      {/* Completeness */}
                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-white/60">Completeness</span>
                          <span className="text-white">{completeness}%</span>
                        </div>
                        <Progress value={completeness} className="h-2" />
                      </div>

                      {/* Sections */}
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(sections).map(([section, complete]) => (
                          <Badge
                            key={section}
                            variant="outline"
                            className={
                              complete
                                ? "bg-green-500/10 text-green-400 border-green-500/30"
                                : "bg-red-500/10 text-red-400 border-red-500/30"
                            }
                          >
                            {complete ? <CheckCircle className="h-3 w-3 mr-1" /> : <AlertCircle className="h-3 w-3 mr-1" />}
                            {section.charAt(0).toUpperCase() + section.slice(1)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-white/20 text-white hover:bg-white/10"
                        onClick={() => navigate(`/admin/cim-review/${cim.dealId}`)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Review
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-white/20 text-white hover:bg-white/10"
                        onClick={() => navigate(`/cim-preview/${cim.dealId}`)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Preview
                      </Button>
                      {cim.status === "approved" || cim.status === "sent_to_client" ? (
                        <Button size="sm" className="bg-brand-gold text-brand-navy hover:bg-brand-gold/90">
                          <Download className="h-4 w-4 mr-2" />
                          PDF
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
